<template>
  <ElConfigProvider :z-index="3000">
    <div>
      <router-view />
      <ConfirmDialog />
    </div>
  </ElConfigProvider>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from "vue";
import { attachedVueElement } from "@/utils/helpers/scanner";
import { useI18n } from "vue-i18n";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import { LogLevel, LogService } from "./services/logging";
import { LocalStorageKeys } from "./services/storage.service";
import { useOrderStore } from "@smeetz/order-store";
import ReceiptModule from "./store/modules/receipt";
import { monitorReceiptSynchingIssues } from "./utils/vue-helpers/receipt-counter";
import * as Sentry from "@sentry/vue";
import AppModule from "./store/modules/app";
import { useIdle } from "@vueuse/core";
import {
  registerUpdate,
  autoUpdate,
  applyPendingUpdate,
  canRefresh,
  checkForUpdate,
  checkReleaseChannel,
  getTotalMemoryUsed,
  hasReloadedForMemoryRecently,
  monitorAppPerformance,
} from "@/utils/vue-helpers/common";
import { loadStatusPage, loadStonly } from "./utils/vue-helpers/scripts";
import {
  logPendingIgnoredAndFailedOrders,
  resetGrowingCollecitonsFromUI,
  resetGrowingCollections,
} from "./utils/vue-helpers/db";
import { ModalStatus } from "./constants/modal";
import ModalModule from "./store/modules/modal";
import { syncLogs } from "./db/poslogs";
import { cashPointClosuresSynchHandler } from "./db/cashpoint-closure";
import { loadOffers } from "@/utils/helpers/offers";
import { checkUserAccessToken } from "@/utils/helpers/auth";
import { MEMORY_USAGE_THRESHOLD } from "./utils/constants";
import { isCustomerDisplayWindow } from "@/utils/setup";
import { setupCustomerDisplaySync } from "@/utils/vue-helpers/customer-display";
import {
  isBelgianCompliant,
  isFrenchCompliant,
  isSpanishCompliant,
} from "./utils/vue-helpers/compliance";
import {
  initializeComplianceLib,
  testEfrConnection,
  testFdmConnection,
} from "./utils/helpers/compliance";
import {
  ensureOperatorSessionV2,
  initializeComplianceV2Lib,
} from "./utils/helpers/compliance-v2";
import AuthModule from "./store/modules/auth";
import UserModule from "./store/modules/user";
import POSConfigModule from "./store/modules/pos-config";
import ConfirmDialog from "@/components/modal/ConfirmDialog.vue";
import { ElConfigProvider } from "element-plus";

// Boot timestamp, used to report how long the compliance init waited for the session.
const appBootedAt = Date.now();

export default defineComponent({
  name: "app",
  components: { ConfirmDialog, ElConfigProvider },
  setup() {
    const orderReference = computed(() => {
      return useOrderStore().selectedOrder?.reference;
    });

    // Skip main window watchers/listeners for customer display
    if (!isCustomerDisplayWindow()) {
      // if the app has been idle for 1 min then we check whether we should update/refresh the app.
      const { idle } = useIdle(1 * 60 * 1000);
      // Apply a pending update once the terminal is idle
      watch(
        () => idle.value && AppModule.isUpdateAvailable,
        (shouldUpdate) => {
          if (!shouldUpdate) return;
          LogService.info("Auto-update triggered due to idle time");
          autoUpdate();
        }
      );
      watch(idle, (isIdle) => {
        if (!isIdle || AppModule.isUpdateAvailable) return;
        const totalMemoryUsed = getTotalMemoryUsed();
        if (totalMemoryUsed <= MEMORY_USAGE_THRESHOLD) {
          AppModule.setIsMemoryHigh(false);
          return;
        }
        // A recent reload did not free the memory: ask the cashier to restart the app (L3-8422)
        if (hasReloadedForMemoryRecently()) {
          if (!AppModule.isMemoryHigh) {
            LogService.info(
              `Memory consumption still at ${totalMemoryUsed} MB after a memory reload, asking the user to restart the app`,
              {},
              LogLevel.Error
            );
            AppModule.setIsMemoryHigh(true);
          }
          return;
        }
        if (AppModule.isRefreshing || !canRefresh()) return;
        LogService.info(
          `Memory consumption reached ${totalMemoryUsed} MB, going to refresh the page`
        );
        localStorage.setItem(
          LocalStorageKeys.LastMemoryReload,
          String(Date.now())
        );
        AppModule.setIsRefreshing(true);
        window.location.reload();
      });

      // Set the order reference as an extra tag for Sentry
      watch(
        orderReference,
        () => {
          Sentry.setTags({
            orderReference: orderReference.value,
          });
        },
        { deep: true }
      );

      /**
       * The compliance lib must not be initialized before the session is fully established.
       * This component mounts at app.mount(), while the router guard is still running
       * updateToken() -> fetchUser(), and SetTokensAfterRefresh clears every token for the
       * duration of its refresh call. Initializing there authorized the device with a stale
       * or missing token, the unauthorized response flipped packageInit to false, and
       * SellingDisabled flashed until a later init succeeded.
       *
       * AuthModule.token is only committed once updateToken() has resolved (and by Login /
       * LoginWithPin), hasUser means fetchUser() returned, and getPosConfigPermission is the
       * same gate the router uses to leave /login.
       *
       * getGroupCountryCode is what makes the country real, and it is a separate condition
       * because getPosConfigPermission does not imply it: that getter reads selectedConfig
       * and short-circuits on UserModule.isAdmin, neither of which touches groupInfo. An
       * admin would otherwise reach this watcher as soon as fetchUser() returned, with the
       * country still "", every isXCompliant false, and the init aborting as "Unsupported
       * country". Waiting for the country is also what lets PosConfiguration drop its own
       * Belgian init call, since this watcher now fires once fetchGroupInfo() has landed.
       */
      const sessionReady = computed(
        () =>
          !!AuthModule.token &&
          UserModule.hasUser &&
          !!POSConfigModule.getPosConfigPermission &&
          !!POSConfigModule.getGroupCountryCode
      );

      watch(
        sessionReady,
        async (ready) => {
          if (!ready) return;
          LogService.info("Compliance: session ready, initializing", {
            msSinceBoot: Date.now() - appBootedAt,
            hasAuthToken: !!AuthModule.token,
            hasUser: UserModule.hasUser,
            hasPosConfigPermission: !!POSConfigModule.getPosConfigPermission,
            countryCode: POSConfigModule.getGroupCountryCode,
          });
          try {
            await initializeComplianceLib("app-boot");
          } catch (error) {
            LogService.info("Failed to initialize compliance lib", { error });
          }
          // France: boot the v2 library fail-soft alongside v1; direct events
          // lazily re-initialize it (and restore the operator session) on
          // first use.
          try {
            await initializeComplianceV2Lib();
          } catch (error) {
            LogService.info("Failed to initialize compliance v2 lib", {
              error,
            });
          }
          // France: the bus-driven SALE is fail-open and cannot restore the
          // operator session lazily the way direct events do — a missing
          // WORK_IN would let a sale complete silently unfiscalized. Restore
          // it proactively as soon as the session is usable (fail-soft, and
          // deduplicated against the login flow's own WORK_IN).
          await ensureOperatorSessionV2();
        },
        { immediate: true }
      );

      // Apply a pending update immediately.
      document.addEventListener("swUpdated", registerUpdate);
      applyPendingUpdate();
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") checkForUpdate();
      });
      window.addEventListener("focus", () => checkForUpdate());
      // The release channel can point to another version at any time (APPS-644)
      setInterval(checkReleaseChannel, 60 * 1000);

      // Broadcast order changes to the customer display window and answer
      // its initial basket request.
      setupCustomerDisplaySync();
    }

    const { t } = useI18n({});

    async function refreshWarning(event: Event) {
      if (ModalModule.status === ModalStatus.PaymentProgress) {
        event.preventDefault();
        event.returnValue = true; // legacy support
        if (!window.electronAPI) {
          return;
        }
        LogService.info(
          "User tried to refresh during a card payment, prompting for confirmation"
        );
        const confirmed = await showConfirm({
          title: t("common.warning"),
          message: t("warning.page-refresh"),
          variant: "warning",
        });

        if (confirmed) {
          LogService.info(`User confirmed refreshing during card payment`);
          ModalModule.setStatus(ModalStatus.Hidden);
          window.location.reload();
        } else {
          LogService.info(`User cancelled refreshing during card payment`);
        }
      }
    }

    return {
      t,
      refreshWarning,
    };
  },
  async mounted() {
    // Skip all main window triggers/listeners for customer display
    if (isCustomerDisplayWindow()) {
      return;
    }

    LogService.info(`Scanner: going to attach scanner to the App vue`);
    attachedVueElement();
    (window as any).resetGrowingCollections = resetGrowingCollections;
    (window as any).resetGrowingCollectionsFromUI =
      resetGrowingCollecitonsFromUI;
    this.triggerSynchingIssuesMonitor();
    const navigation = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    monitorAppPerformance(navigation?.type);
    this.triggerAppPerformanceMonitoring();
    if (ReceiptModule.receiptCounter) {
      this.triggerReceiptCountersMonitoring();
    }
    loadStonly();
    loadStatusPage();
    window.addEventListener("beforeunload", this.refreshWarning);
    this.triggerLogsSync();
    this.triggerCashPointClosureSync();
    this.triggerAccessTokenCheck();
    this.checkEfrConnection();
    try {
      loadOffers();
      LogService.info("Offers object instantiated");
    } catch (error) {
      LogService.info("Failed to instantiate offers object", { error });
    }
    // The compliance init used to run here. It is now driven by the sessionReady watcher in
    // setup(); this line makes the deferral visible in the logs rather than looking like the
    // init silently disappeared.
    LogService.info("Compliance: boot init deferred until session is ready", {
      hasAuthToken: !!AuthModule.token,
      hasUser: UserModule.hasUser,
      hasPosConfigPermission: !!POSConfigModule.getPosConfigPermission,
      countryCode: POSConfigModule.getGroupCountryCode,
    });
  },
  methods: {
    triggerSynchingIssuesMonitor() {
      const periodMilliSeconds = 1000 * 60 * 5;
      setInterval(() => {
        logPendingIgnoredAndFailedOrders();
      }, periodMilliSeconds);
    },
    triggerReceiptCountersMonitoring() {
      const periodMilliSeconds = 1000 * 60 * 5;
      setInterval(() => monitorReceiptSynchingIssues(), periodMilliSeconds);
    },
    triggerAppPerformanceMonitoring() {
      // Each 15 minutes we monitor how is the app doing
      const periodMilliSeconds = 1000 * 60 * 10;
      setInterval(() => monitorAppPerformance(), periodMilliSeconds);
    },
    triggerLogsSync() {
      const periodMilliSeconds = 1000 * 5;
      setInterval(() => syncLogs(), periodMilliSeconds);
    },
    triggerCashPointClosureSync() {
      const periodMilliSeconds = 1000 * 30;
      setInterval(() => cashPointClosuresSynchHandler(), periodMilliSeconds);
    },
    triggerAccessTokenCheck() {
      const periodMilliSeconds = 30 * 60 * 1000;
      setInterval(() => checkUserAccessToken(), periodMilliSeconds);
    },
    checkEfrConnection() {
      // check the fiscal device connection every 5 seconds
      const periodMilliSeconds = 5 * 1000;
      setInterval(async () => {
        if (isBelgianCompliant.value) {
          // Belgium talks to the FDM rather than a local EFR server
          await testFdmConnection();
          return;
        }
        if (!isSpanishCompliant.value && !isFrenchCompliant.value) {
          // applies only to Spain & France
          return;
        }
        await testEfrConnection();
      }, periodMilliSeconds);
    },
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.refreshWarning);
  },
});
</script>
